import { useRequiredContext } from "@redotech/react-util/context";
import {
  UpsellConditionSource,
  UpsellConditionStep,
} from "@redotech/redo-flows-common/steps";
import { t, useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  ProductSelectionModal,
  storefrontProductToModalProduct,
} from "@redotech/redo-web/product/product-selection-modal";
import { searchProducts } from "@redotech/shopify-storefront";
import { useState } from "react";
import { FrontendStepType, PartialWithRequiredType } from ".";
import { CollectionSelect } from "../../../../conversion/post-purchase/builder/post-purchase-elements/collection-select";

export const upsellConditionStepType: FrontendStepType<
  PartialWithRequiredType<UpsellConditionStep>
> = {
  valid: (step) => {
    const hasProductsDefined =
      !!step.expression?.products && step.expression.products.length > 0;
    const hasCollectionDefined = !!step.expression?.collection;
    return step.isDefault || hasProductsDefined || hasCollectionDefined;
  },
  type: "standard",
  inspectorFactory: (props) => ({
    header: { title: props.step.name ?? t("Post purchase upsell") },
    sections: props.step.isDefault
      ? []
      : [
          {
            fields: [
              {
                path: "name",
                description: t("Name of the segment you're building"),
                widget: "text",
              },
              {
                path: "expression.source",
                label: t("Condition"),
                widget: "custom",
                Element: (props) => {
                  const { t } = useTranslation();
                  const options: RedoListItem<UpsellConditionSource>[] =
                    Object.values(UpsellConditionSource)
                      .map((source) => ({
                        id: source,
                        type: "text" as const,
                        text:
                          source === UpsellConditionSource.PRODUCT
                            ? t("Has specific product in cart")
                            : t("Has specific collection in cart"),
                        value: source,
                      }))
                      .sort((a, b) => a.text.localeCompare(b.text));

                  return (
                    <RedoSingleSelectDropdownInput
                      options={options}
                      optionSelected={({ item }) => {
                        props.valueChange({
                          ...props.value,
                          expression: {
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            ...props.value.expression!,
                            source: item.value,
                          },
                        });
                      }}
                      placeholder={t("Select an option")}
                      selectedItem={options.find(
                        (o) => o.value === props.value.expression?.source,
                      )}
                      size="xs"
                    />
                  );
                },
              },
              {
                path: "expression.product",
                label: t("Products"),
                visibleIf: (props) =>
                  props.expression?.source === UpsellConditionSource.PRODUCT,
                widget: "custom",
                Element: (props) => {
                  const storefrontClient = useRequiredContext(
                    StorefrontClientContext,
                  );
                  const [modalOpen, setModalOpen] = useState(false);

                  return (
                    <>
                      {props.value.expression?.products &&
                        props.value.expression.products.map((product, i) => {
                          return (
                            <RedoButton
                              hierarchy="secondary"
                              IconTrailing={() => <TrashIcon />}
                              key={i}
                              onClick={() =>
                                props.valueChange({
                                  ...props.value,
                                  expression: {
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    ...props.value.expression!,
                                    products: (
                                      props.value.expression?.products ?? []
                                    ).filter((p) => p?.id !== product?.id),
                                  },
                                })
                              }
                              size="sm"
                              text={product?.name}
                            />
                          );
                        })}
                      <RedoButton
                        hierarchy="secondary"
                        onClick={() => setModalOpen(true)}
                        size="sm"
                        text={t("Add product")}
                      />
                      <ProductSelectionModal
                        open={modalOpen}
                        searchProducts={async ({ search }) => {
                          const results = await searchProducts(
                            storefrontClient,
                            search,
                            100,
                          );
                          return results
                            ? results.map(storefrontProductToModalProduct)
                            : undefined;
                        }}
                        setOpen={setModalOpen}
                        setSelected={(selected) => {
                          if (selected) {
                            props.valueChange({
                              ...props.value,
                              expression: {
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                ...props.value.expression!,
                                products: [
                                  ...(props.value.expression?.products ?? []),
                                  ...selected.map((s) => ({
                                    id: s.product.id,
                                    name: s.product.title,
                                  })),
                                ],
                              },
                            });
                          }
                        }}
                      />
                    </>
                  );
                },
              },
              {
                path: "expression.collection",
                label: t("Collection"),
                visibleIf: (props) =>
                  props.expression?.source === UpsellConditionSource.COLLECTION,
                widget: "custom",
                Element: (props) => {
                  return (
                    <CollectionSelect
                      value={props.value.expression?.collection}
                      valueChange={(newVal) =>
                        props.valueChange({
                          ...props.value,
                          expression: {
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            ...props.value.expression!,
                            collection: newVal,
                          },
                        })
                      }
                    />
                  );
                },
              },
            ],
          },
        ],
  }),
};
